import { useState } from "react";
import * as Icons from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

// Lista de ícones populares do lucide-react (sem duplicatas)
const popularIcons = [
  "Package", "ShoppingCart", "Store", "Box", "Grid3x3", "Layers", "Folder",
  "Tag", "Tags", "Bookmark", "Star", "Heart", "Gift", "Crown", "Award",
  "Trophy", "Medal", "Zap", "Flame", "Sun", "Moon", "Cloud", "Droplet",
  "Leaf", "Flower", "TreePine", "Apple", "Banana", "Carrot", "Coffee",
  "Utensils", "Pizza", "IceCream", "Cake", "Cookie", "Candy", "Wine",
  "Beer", "Cocktail", "Home", "Building", "Building2", "Factory", "Warehouse",
  "Shop", "ShoppingBag", "ShoppingBasket", "Basket", "BasketShopping",
  "Truck", "Car", "Bike", "Plane", "Ship", "Train", "Bus", "Taxi",
  "Music", "Headphones", "Radio", "TV", "Monitor", "Laptop", "Smartphone",
  "Tablet", "Camera", "Video", "Gamepad2", "Dice1", "Dice2", "Dice3",
  "Dice4", "Dice5", "Dice6", "Puzzle", "PuzzleIcon", "Palette", "Brush",
  "Pen", "Pencil", "Marker", "Highlighter", "Eraser", "Scissors", "Ruler",
  "Compass", "Map", "MapPin", "Navigation", "Globe", "Earth", "World",
  "Flag", "FlagTriangleRight", "Badge", "Shield", "ShieldCheck",
  "Lock", "Unlock", "Key", "KeyRound", "Fingerprint", "Eye", "EyeOff",
  "Bell", "BellRing", "Megaphone", "Speaker", "Volume2", "VolumeX",
  "Mic", "MicOff", "VideoOff", "Phone", "PhoneCall", "PhoneIncoming",
  "PhoneOutgoing", "PhoneMissed", "MessageCircle", "MessageSquare", "Chat",
  "Mail", "Inbox", "Send", "Paperclip", "Link", "Link2", "ExternalLink",
  "Download", "Upload", "Share", "Share2", "Copy", "Paste", "Clipboard",
  "File", "FileText", "FileImage", "FileVideo", "FileAudio", "FileCode",
  "FolderOpen", "FolderPlus", "FolderMinus", "Archive",
  "Database", "Server", "HardDrive", "Cpu", "MemoryStick", "Wifi", "WifiOff",
  "Bluetooth", "BluetoothConnected", "Signal", "SignalHigh", "SignalLow",
  "Battery", "BatteryFull", "BatteryLow", "BatteryCharging", "Plug", "PlugZap",
  "Power", "PowerOff", "Lightbulb", "Lamp", "Flashlight", "FlashlightOff",
  "Candle", "Fire", "Droplets", "Waves", "Wind",
  "Snowflake", "CloudRain", "CloudSnow", "CloudLightning", "CloudDrizzle",
  "Sparkles", "Rainbow", "Umbrella", "Tent",
  "TreeDeciduous", "Flower2", "Sprout",
  "Cherry", "Grape", "Lemon", "Orange",
  "Peach", "Pear", "Strawberry", "Watermelon", "Tea",
  "Milk", "Burger", "HotDog", "Sandwich", "Taco", "Sushi", "Fish",
  "Chicken", "Beef", "Egg", "Bread", "Croissant", "Donut", "Muffin",
  "Cheese", "Butter", "Honey", "Salt", "Pepper", "Sugar", "Flour",
  "UtensilsCrossed", "ChefHat", "Soup", "Pot", "Pan",
  "ForkKnife", "Juice", "Water", "Soda", "Energy", "Protein", "Vitamin", "Pill",
  "Syringe", "Stethoscope", "HeartPulse", "Activity", "Pulse",
  "Thermometer", "Bandage", "BandAid", "Cross", "Plus",
  "Minus", "X", "Check", "CheckCircle", "CheckSquare", "XCircle", "XSquare",
  "AlertCircle", "AlertTriangle", "Info", "HelpCircle", "QuestionMark",
  "Circle", "Square", "Triangle", "Diamond", "Hexagon", "Octagon",
  "Pentagon", "Ellipse", "Rectangle", "RectangleHorizontal", "RectangleVertical",
];

interface IconPickerProps {
  value?: string;
  onValueChange: (iconName: string) => void;
  trigger?: React.ReactNode;
}

export function IconPicker({ value, onValueChange, trigger }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Converter nome do ícone para PascalCase
  const toPascalCase = (str: string) => {
    return str
      .split(/[-_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("");
  };

  // Obter componente do ícone
  const getIconComponent = (iconName: string) => {
    const pascalName = toPascalCase(iconName);
    return (Icons as Record<string, any>)[pascalName];
  };

  // Filtrar ícones baseado na busca e remover duplicatas
  const filteredIcons = Array.from(new Set(
    popularIcons.filter((icon) =>
      icon.toLowerCase().includes(searchTerm.toLowerCase())
    )
  ));

  // Renderizar ícone selecionado ou placeholder
  const SelectedIcon = value ? getIconComponent(value) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
          >
            {SelectedIcon ? (
              <>
                <SelectedIcon className="mr-2 h-4 w-4" />
                {value}
              </>
            ) : (
              <>Selecionar ícone</>
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Selecionar Ícone</DialogTitle>
          <DialogDescription>
            Escolha um ícone para a categoria. Use a busca para encontrar ícones específicos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar ícone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <ScrollArea className="h-[400px]">
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 p-2">
              {filteredIcons.map((iconName) => {
                const IconComponent = getIconComponent(iconName);
                if (!IconComponent) return null;

                const isSelected = value === iconName;

                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => {
                      onValueChange(iconName);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center p-2 rounded-lg border transition-colors min-h-[80px]",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-muted border-border"
                    )}
                    title={iconName}
                  >
                    <IconComponent className="h-5 w-5 mb-1" />
                    <span className="text-[10px] truncate w-full text-center leading-tight">
                      {iconName}
                    </span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}

